import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';

import useMicroform, {
  postBridgeMessage,
  signalTokenSuccess,
  useBridgeRegistration,
  useCardType,
  usePublicKeyRefresh
} from '../../hooks/useMicroform/useMicroform';
import { getCardTypeFromMicroform, getCVVLength, validateForm } from '../../utils/creditCard/creditCard';
import { handleEmptyObjects } from '../../utils/handleEmptyObjects/handleEmptyObjects';
import MicroformInputs from '../MicroformInputs/MicroformInputs';
import * as utils from './MicroformAppWrapper';

const defaultMicroformProps = {
  creditCardNumber: { name: 'creditCardNumber', label: 'Credit card number', clearAccessbilityLabel: 'Clear', type: 'tel', validations: [{ message: 'Enter a valid 16-digit credit card number.' }] },
  expirationDate: { name: 'expirationDate', label: 'Expiration date', placeholderLabel: 'MM/YY', clearAccessbilityLabel: 'Clear', type: 'tel', validations: [{ message: 'Please enter a valid date' }] },
  cvv: { name: 'cvv', label: 'Security code', subtitle: '3 digits on the back of your card', clearAccessbilityLabel: 'Clear', type: 'tel', validations: [{ message: 'Please enter correct CVV code' }] },
  supportedCards: [{ type: 'Visa', usesCVV: true, usesExpirationDate: true, image: 'visa', cvvImage: 'visa-cvv', maxCVVLength: 3 }]
};

const defaultCvvMicroformProps = {
  creditCardNumber: { name: 'creditCardNumber', label: 'Credit card number', clearAccessbilityLabel: 'Clear', type: 'tel' },
  expirationDate: { name: 'expirationDate', label: 'Expiration date', placeholderLabel: 'MM/YY', clearAccessbilityLabel: 'Clear', type: 'tel' },
  cvv: { name: 'cvv', label: 'Security code', subtitle: '3 digits on the back of your card', clearAccessbilityLabel: 'Clear', type: 'tel' },
  supportedCards: [{ type: 'Visa', usesCVV: true, usesExpirationDate: true, image: 'visa', cvvImage: 'visa-cvv', maxCVVLength: 3 }]
};

export const MicroformAppWrapper = ( props ) => {
  const { isCvvOnly, showPaymentFieldsInitially, defaultProps: customConfig } = handleEmptyObjects( props );
  const fieldConfig = customConfig || ( isCvvOnly ? defaultCvvMicroformProps : defaultMicroformProps );

  const [initDetails, setInitDetails] = useState( {} );
  const formikRef = useRef( null );

  useBridgeRegistration( { setInitDetails, formikRef, isCvvOnly } );


  const publicKey = isCvvOnly ?
    initDetails?.creditCardInput?.encryptionKey?.publicKey :
    initDetails?.encryptionKey?.publicKey;

  usePublicKeyRefresh( publicKey );

  const [cardType, setCardType] = useCardType( { isCvvOnly, initDetails } );

  const { microform, ready: microformReady, error: microformError } = useMicroform( true, publicKey );

  const [microformNumberData, setMicroformNumberData] = useState( { empty: true } );
  const [microformCVVData, setMicroformCVVData] = useState( { empty: true } );

  const handleMicroformNumberChange = useCallback( ( data ) => {
    if( isCvvOnly ){
      return;
    }

    setMicroformNumberData( data );

    const type = utils.resolveCardType( {
      microformData: data,
      initDetails,
      defaultProps: fieldConfig
    } );
    setCardType( type );
  }, [initDetails, isCvvOnly, fieldConfig, setCardType] );

  const handleMicroformCVVChange = useCallback( ( data ) => {
    setMicroformCVVData( data );
  }, [] );

  useEffect( () => {
    formikRef.current?.validateForm?.();
  }, [microformNumberData?.valid, microformNumberData?.empty, microformCVVData?.valid, microformCVVData?.empty] );

  const isLoading = !publicKey || !microformReady;

  const isCvvRequired = utils.getIsCvvRequired( {
    isCvvOnly,
    showPaymentFieldsInitially,
    cardType,
    microformNumberData
  } );

  const formProps = ( isCvvOnly ? initDetails?.creditCardInput?.creditCardForm : initDetails?.creditCardForm ) || {};

  const cvvLength = utils.getResolvedCvvLength( {
    cardType,
    microformNumberData,
    props,
    isCvvOnly,
    initDetails,
    fieldConfig
  } );

  const components = {
    creditCard: {
      input: !isCvvOnly,
      dateOrCvv: isCvvRequired,
      cvv: isCvvRequired,
      expirationDate: !isCvvOnly && isCvvRequired,
      microformNumber: isCvvOnly ? undefined : microformNumberData,
      microformCVV: microformCVVData
    }
  };

  const onClearNumber = useCallback( isCvvOnly ? () => {} : () => setMicroformNumberData( { empty: true } ), [isCvvOnly] );
  const onClearCvv = useCallback( () => setMicroformCVVData( { empty: true } ), [] );
  const scheduleValidationStatusUpdate = useCallback( () => {}, [] );

  const formClassName = isCvvOnly ? 'MicroformCVVAppWrapper' : 'MicroformAppWrapper';
  const encryptionKey = isCvvOnly ? initDetails?.creditCardInput?.encryptionKey : initDetails?.encryptionKey;

  return (
    <Formik
      innerRef={ formikRef }
      initialValues={ isCvvOnly ? { cvv: '' } : { creditCardNumber: '', expirationDate: '', cvv: '' } }
      onSubmit={ ( values, methods ) => {
        utils.handleSubmit(
          { values, microform, initDetails, isCvvOnly, cardType },
          methods
        );
      } }
      validate={ ( values ) => utils.handleValidate( {
        values,
        cardType,
        isCvvOnly,
        microformNumberData,
        microformCVVData,
        isCvvRequired,
        initDetails,
        fieldConfig
      } ) }
    >
      { () => (
        <Form className={ formClassName } >
          <MicroformInputs
            { ...fieldConfig }
            { ...formProps }
            cvvLength={ cvvLength }
            encryptionKey={ encryptionKey }
            components={ components }
            cardType={ cardType }
            creditCardFormData={ {} }
            cyberCCResponse={ {} }
            isSubmitting={ false }
            isCVVApplied={ false }
            isUpdateCard={ false }
            isMaskedCard={ false }
            microform={ microform }
            microformReady={ isLoading ? false : microformReady }
            microformError={ microformError }
            propsLoading={ isLoading }
            loading={ isLoading }
            selected={ true }
            transientToken={ true }
            handleMicroformNumberChange={ handleMicroformNumberChange }
            handleMicroformCVVChange={ handleMicroformCVVChange }
            onClearNumber={ onClearNumber }
            onClearCvv={ onClearCvv }
            displayOverlay={ false }
            scheduleValidationStatusUpdate={ scheduleValidationStatusUpdate }
          />
        </Form>
      ) }
    </Formik>
  );
};

/**
 * Determines if the CVV field is required based on mode and card detection.
 *
 * @param {object} data - Arguments
 * @param {boolean} data.isCvvOnly - CVV only mode flag
 * @param {boolean} data.showPaymentFieldsInitially - Displays fields initially flag
 * @param {object} data.cardType - Detected card type details
 * @param {object} data.microformNumberData - Change data from credit card number field
 * @returns {boolean}
 */
export const getIsCvvRequired = ( data ) => {
  const { isCvvOnly, showPaymentFieldsInitially, cardType, microformNumberData } = handleEmptyObjects( data );

  if( isCvvOnly ){
    return true;
  }
  if( showPaymentFieldsInitially ){
    return true;
  }
  return !!cardType?.type && typeof microformNumberData?.card?.[0]?.securityCode === 'object';
};

/**
 * Resolves the appropriate CVV length based on card detection and initialization.
 *
 * @param {object} data - Arguments
 * @param {object} data.cardType - Detected card type details
 * @param {object} data.microformNumberData - Change data from credit card number field
 * @param {object} data.props - Component props
 * @param {boolean} data.isCvvOnly - CVV only mode flag
 * @param {object} data.initDetails - Initial details from native app
 * @param {object} data.fieldConfig - Base field configuration
 * @returns {number}
 */
export const getResolvedCvvLength = ( data ) => {
  const { cardType, microformNumberData, props, isCvvOnly, initDetails, fieldConfig } = handleEmptyObjects( data );

  if( cardType?.maxCVVLength ){
    return cardType.maxCVVLength;
  }

  const cvvSource = isCvvOnly ? initDetails?.creditCardInput?.creditCardForm?.cvv : ( initDetails?.creditCardForm?.cvv || fieldConfig?.cvv );

  return getCVVLength( microformNumberData, {
    ...props,
    cardTypeImage: cardType?.image || cardType?.type,
    savedCardType: cardType?.type,
    cvv: cvvSource
  } );
};

/**
 * Resolves the card type based on microform data and initialization details.
 *
 * @param {object} data - Arguments
 * @param {object} data.microformData - Data from microform change event
 * @param {object} data.initDetails - Initial details from native app
 * @param {object} data.defaultProps - Default microform props
 * @returns {object} Decorated card type object
 */
export const resolveCardType = ( data ) => {
  const { microformData, initDetails, defaultProps } = handleEmptyObjects( data );

  const cardsList = microformData?.card || [];
  let resolvedCardName = '';

  if( cardsList.length === 1 ){
    resolvedCardName = cardsList[0]?.name;
  }
  else if( cardsList.length === 2 ){
    const hasUltaMC = cardsList.some( ( c ) => c.name === 'ulta' );
    const hasMC = cardsList.some( ( c ) => c.name === 'mastercard' );
    if( hasUltaMC && hasMC ){
      resolvedCardName = 'ultamc';
    }
  }

  const supportedCards = initDetails?.creditCardForm?.supportedCards ||
                        initDetails?.supportedCards ||
                        defaultProps?.supportedCards || [];

  const type = getCardTypeFromMicroform( {
    microformCardType: resolvedCardName,
    transientToken: true,
    value: '',
    supportedCards
  } );

  if( type?.type ){
    return type;
  }

  const brandedName = microformData?.card?.[0]?.brandedName || '';
  return { ...type, type: resolvedCardName ? brandedName : '' };
};

/**
 * Extracts and cleans expiration date parts
 */
export const getExpirationDateParts = ( data ) => {
  const { values, initDetails, isCvvOnly } = handleEmptyObjects( data );

  let rawExpirationDate = '';
  if( isCvvOnly ){
    rawExpirationDate = values?.expirationDate || initDetails?.creditCardInput?.creditCardForm?.expirationDate?.value || initDetails?.selectedCard?.expirationDate || '';
  }
  else {
    rawExpirationDate = values?.expirationDate || '';
  }

  const [expMonth, rawExpYear] = rawExpirationDate.split( '/' );
  const cleanExpMonth = expMonth?.trim() || '';
  const cleanExpYear = rawExpYear?.trim() || '';

  let expirationYear = cleanExpYear;
  if( cleanExpYear.length === 2 ){
    expirationYear = `20${cleanExpYear}`;
  }

  return {
    expirationMonth: cleanExpMonth,
    expirationYear
  };
};

/**
 * Handles the form submission by creating a token via Microform and signaling success/failure to the bridge.
 *
 * @param {object} data - Arguments
 * @param {object} data.values - Current form values
 * @param {object} data.microform - Initialized Microform instance
 * @param {object} data.initDetails - Initial details from native app
 * @param {boolean} data.isCvvOnly - CVV only mode flag
 * @param {object} data.cardType - Detected card type details
 * @param {object} methods - Formik methods
 * @param {function} methods.setSubmitting - Formik's setSubmitting function
 */
export const handleSubmit = ( data, methods ) => {
  const { values, microform, initDetails, isCvvOnly, cardType } = data;
  const { setSubmitting } = methods;

  if( !microform ){
    postBridgeMessage( { method: 'onTokenFailure' } );
    setSubmitting( false );
    return;
  }

  const { expirationMonth, expirationYear } = utils.getExpirationDateParts( { values, initDetails, isCvvOnly } );

  if( !expirationMonth || !expirationYear ){
    if( !isCvvOnly ){
      // eslint-disable-next-line no-console
      console.error( '[NativeBridge] Missing expiration month or year' );
    }
    postBridgeMessage( { method: 'onTokenFailure' } );
    setSubmitting( false );
    return;
  }

  // Ulta's Cybersource setup may require the billing address schema to be present in the createToken call,
  // even if the fields are empty or not collected in this specific wrapper flow.
  const billingAddress = isCvvOnly ? {} : {
    firstName: values?.firstName || '',
    lastName: values?.lastName || '',
    address1: values?.address1 || '',
    address2: values?.address2 || '',
    locality: values?.city || '',
    administrativeArea: values?.state || '',
    postalCode: values?.postalCode || '',
    phoneNumber: values?.phoneNumber || '',
    country: 'US'
  };

  const createTokenParams = {
    expirationMonth,
    expirationYear,
    ...billingAddress
  };

  microform.createToken(
    createTokenParams,
    ( error, response ) => {
      setSubmitting( false );

      if( error?.reason === 'CREATE_TOKEN_VALIDATION_SERVERSIDE' ){
        postBridgeMessage( { method: 'refreshCaptureContext' } );
        return;
      }

      if( error || !response ){
        const errorMsg = typeof error === 'string' ? error : ( error?.reason || error?.message || JSON.stringify( error ) || 'Unknown tokenization error' );
        postBridgeMessage( { method: 'onTokenFailure' } );
        return;
      }

      const tokenString = typeof response === 'string' ? response : JSON.stringify( response );
      signalTokenSuccess( {
        token: tokenString,
        expirationDate: values?.expirationDate,
        creditcardType: cardType?.type || '',
        isCvvOnly
      } );
    }
  );
};

/**
 * Validates the form fields, combining standard credit card validation with Microform field states.
 *
 * @param {object} data - Arguments
 * @param {object} data.values - Current form values
 * @param {object} data.cardType - Detected card type details
 * @param {boolean} data.isCvvOnly - CVV only mode flag
 * @param {object} data.microformNumberData - Change data from credit card number field
 * @param {object} data.microformCVVData - Change data from CVV field
 * @param {boolean} data.isCvvRequired - Whether the CVV field is required
 * @param {object} data.initDetails - Initial details from native app
 * @param {object} data.fieldConfig - Base field configuration
 * @returns {object} Error messages object
 */
export const handleValidate = ( data ) => {
  const { values, cardType, isCvvOnly, microformNumberData, microformCVVData, isCvvRequired, initDetails, fieldConfig } = data;

  const expirationSource = isCvvOnly ? undefined : ( initDetails?.creditCardForm?.expirationDate || fieldConfig?.expirationDate );
  const numberSource = isCvvOnly ? undefined : ( initDetails?.creditCardForm?.creditCardNumber || fieldConfig?.creditCardNumber );
  const cvvSource = isCvvOnly ? initDetails?.creditCardInput?.creditCardForm?.cvv : ( initDetails?.creditCardForm?.cvv || fieldConfig?.cvv );

  const errors = validateForm( {
    values,
    cardType,
    transientToken: true,
    ccNumberEmpty: isCvvOnly ? true : microformNumberData.empty,
    cvvEmpty: microformCVVData.empty,
    ccNumberValid: isCvvOnly ? true : microformNumberData.valid,
    cvvValid: microformCVVData.valid,
    isCvvVisible: isCvvRequired,
    isCcNumberVisible: !isCvvOnly,
    expirationDate: expirationSource,
    creditCardNumber: numberSource,
    cvv: cvvSource
  } );

  if( !isCvvOnly && isCvvRequired && !values?.expirationDate ){
    errors.expirationDate = 'Required';
  }
  return errors;
};

/**
 * @property {object} MicroformAppWrapper - Set the form for app team
 * @property {bool} isCvvOnly - required to check loggedin/annonymous flow
 * @property {bool} showPaymentFieldsInitially - displays expiry and cvv field initially
 */
MicroformAppWrapper.propTypes = {
  isCvvOnly: PropTypes.bool,
  showPaymentFieldsInitially: PropTypes.bool
};

export const defaultProps = {
  isCvvOnly: false,
  showPaymentFieldsInitially: true
};

MicroformAppWrapper.defaultProps = defaultProps;

export default MicroformAppWrapper;